/**
 * Insurer Portal - Dashboard
 * Leads management, products, performance, compliance
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import {
  Shield,
  Users,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Phone,
  Mail,
  Car,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Loader2,
  Eye,
  BadgeCheck
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsurerDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [insurer, setInsurer] = useState(null);
  const [provider, setProvider] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [updatingLead, setUpdatingLead] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('insurer_token');
    if (!token) {
      navigate('/insurer/login');
      return;
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('insurer_token');
      
      const response = await axios.get(`${API_URL}/api/insurance/insurer/dashboard`, {
        params: { insurer_token: token }
      });
      
      if (response.data.success) {
        setInsurer(response.data.insurer);
        setProvider(response.data.provider);
        setLeads(response.data.leads || []);
        setStats(response.data.stats || {});
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('insurer_token');
        navigate('/insurer/login');
      } else {
        toast({ title: "Error loading dashboard", variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLeadStatus = async (leadId, newStatus) => {
    try {
      setUpdatingLead(leadId);
      const token = localStorage.getItem('insurer_token');
      
      await axios.patch(
        `${API_URL}/api/insurance/insurer/leads/${leadId}/status`,
        newStatus,
        { 
          params: { insurer_token: token },
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      toast({ title: `Status updated to ${newStatus}` });
      fetchDashboardData();
    } catch (error) {
      toast({ title: "Failed to update status", variant: "destructive" });
    } finally {
      setUpdatingLead(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('insurer_token');
    localStorage.removeItem('insurer_data');
    navigate('/insurer/login');
  };

  const getStatusBadge = (status) => {
    const config = {
      submitted: { color: 'bg-blue-100 text-blue-700', label: 'New' },
      contacted: { color: 'bg-yellow-100 text-yellow-700', label: 'Contacted' },
      quoted: { color: 'bg-purple-100 text-purple-700', label: 'Quoted' },
      policy_issued: { color: 'bg-green-100 text-green-700', label: 'Policy Issued' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-700', label: status };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900">{insurer?.legal_name || 'Insurer'}</h1>
                <p className="text-sm text-gray-500">
                  {insurer?.status === 'active' ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <BadgeCheck className="h-3 w-3" /> Verified Partner
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-yellow-600">
                      <Clock className="h-3 w-3" /> Pending Approval
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchDashboardData}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Pending Approval Notice */}
        {insurer?.status !== 'active' && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              Your account is pending approval. Once verified, your insurance products will be visible to buyers.
              Verification Score: <strong>{insurer?.verification?.risk_score || 0}/100</strong>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold">{stats.total_leads || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-500">Quotes Sent</p>
                  <p className="text-2xl font-bold">{stats.quoted || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-sm text-gray-500">Policies Issued</p>
                  <p className="text-2xl font-bold">{stats.policies_issued || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-emerald-500" />
                <div>
                  <p className="text-sm text-gray-500">Conversion</p>
                  <p className="text-2xl font-bold">{stats.conversion_rate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="leads" className="space-y-4">
          <TabsList>
            <TabsTrigger value="leads">
              <Users className="h-4 w-4 mr-1" /> Leads
            </TabsTrigger>
            <TabsTrigger value="products">
              <FileText className="h-4 w-4 mr-1" /> Products
            </TabsTrigger>
            <TabsTrigger value="performance">
              <BarChart3 className="h-4 w-4 mr-1" /> Performance
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Quote Requests</CardTitle>
                <CardDescription>Manage incoming insurance enquiries</CardDescription>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No leads yet. They'll appear here when buyers request quotes.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map(lead => (
                        <TableRow key={lead.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{lead.buyer_name}</p>
                              <p className="text-xs text-gray-500">{lead.buyer_email}</p>
                              <p className="text-xs text-gray-500">{lead.buyer_phone}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Car className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-sm">{lead.vehicle_make} {lead.vehicle_model}</p>
                                <p className="text-xs text-gray-500">
                                  Rs. {lead.vehicle_value?.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{lead.policy_type}</Badge>
                            <p className="text-xs text-gray-500 mt-1">{lead.usage_type}</p>
                          </TableCell>
                          <TableCell>{getStatusBadge(lead.status)}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={lead.status}
                              onValueChange={(v) => handleUpdateLeadStatus(lead.id, v)}
                              disabled={updatingLead === lead.id}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="submitted">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="quoted">Quoted</SelectItem>
                                <SelectItem value="policy_issued">Policy Issued</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Products</CardTitle>
                <CardDescription>Your active policy types and coverage options</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <Label className="text-gray-500">Policy Types</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {provider?.policy_types?.map(type => (
                        <Badge key={type} variant="secondary">{type}</Badge>
                      )) || <p className="text-sm text-gray-400">Not set</p>}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <Label className="text-gray-500">Vehicle Types</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {provider?.vehicle_types_covered?.map(type => (
                        <Badge key={type} variant="outline">{type}</Badge>
                      )) || <p className="text-sm text-gray-400">Not set</p>}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <Label className="text-gray-500">Usage Types</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {provider?.usage_types?.map(type => (
                        <Badge key={type} variant="outline">{type}</Badge>
                      )) || <p className="text-sm text-gray-400">Not set</p>}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <Label className="text-gray-500">Add-ons</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {provider?.add_ons_available?.map(addon => (
                        <Badge key={addon} className="bg-purple-100 text-purple-700">{addon}</Badge>
                      )) || <p className="text-sm text-gray-400">None</p>}
                    </div>
                  </div>
                </div>
                
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-800">
                    To update your products and coverage, please contact support or update via your profile settings.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle>Performance Analytics</CardTitle>
                <CardDescription>Track your conversion and engagement metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{stats.total_leads || 0}</p>
                    <p className="text-sm text-blue-700">Total Enquiries</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-3xl font-bold text-purple-600">{stats.quoted || 0}</p>
                    <p className="text-sm text-purple-700">Quotes Sent</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{stats.policies_issued || 0}</p>
                    <p className="text-sm text-green-700">Policies Issued</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-3xl font-bold text-emerald-600">{stats.conversion_rate || 0}%</p>
                    <p className="text-sm text-emerald-700">Conversion Rate</p>
                  </div>
                </div>
                
                {provider?.trust_score !== undefined && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-indigo-800">Trust Score</span>
                      <span className="text-2xl font-bold text-indigo-600">{provider.trust_score}/100</span>
                    </div>
                    <p className="text-sm text-indigo-700">
                      {provider.trust_score >= 80 ? 'Excellent standing! Eligible for "Trusted Insurer" badge.' :
                       provider.trust_score >= 60 ? 'Good standing. Keep up the performance!' :
                       'Build trust by responding quickly and improving conversion.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default InsurerDashboard;
